import React, { useState, useRef, useEffect } from "react";
import Button from "../Buttons/Button";
import FormFieldSet from "../FormFields/FormFieldSet";
import Chip from "../Chip/Chip";

const CustomSearchNew = ({ value, onChange, names = [], title }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const [inputTitle, setInputTitle] = useState(title || "")
  useEffect(() => {
    setInputTitle(title);
  }, [title]);
  // Use the prop directly instead of internal state

  let searchValue = value;

  const filteredNames =
    searchValue.length > 0
      ? names
        .filter((name) =>
          name.toLowerCase().includes(searchValue.toLowerCase())
        )
        .slice(0, 10)
      : [];


  const handleInputChange = (e) => {
    setInputTitle("")
    const inputValue = e.target.value;
    setIsOpen(inputValue.length > 0);
    setHighlightedIndex(-1);

    if (onChange) {
      onChange(e.target.value);
      // if (keyWord.length < 0) {
      //   keyWord = "\"\""
      // }
    }
  };

  const handleSelect = (name) => {
    setIsOpen(false);
    setHighlightedIndex(-1);

    if (onChange) {
      onChange(name);
    }
  };

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!isOpen) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < filteredNames.length - 1 ? prev + 1 : 0
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev > 0 ? prev - 1 : filteredNames.length - 1
        );
        break;
      case "Enter":
        e.preventDefault();
        if (highlightedIndex >= 0) {
          handleSelect(filteredNames[highlightedIndex]);
        }
        break;
      case "Escape":
        setIsOpen(false);
        setHighlightedIndex(-1);
        break;
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setIsOpen(false);
        setHighlightedIndex(-1);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="w-full flex justify-center my-10">
      <div className="w-full max-w-[500px] relative" ref={containerRef}>
        <div className="bg-white">
          <input
            ref={inputRef}
            type="text"
            name="search keyword"
            value={searchValue || inputTitle}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder="Enter Keyword"
            className="w-full px-2 py-1 text-xs border border-gray-200 hover:border-primary focus:border-primary text-black 
              placeholder:text-gray outline-none rounded bg-transparent"
          />
        </div>

        {isOpen && filteredNames.length > 0 && (
          <div className="absolute left-0 right-0 mt-2 z-50 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
            {filteredNames.map((name, index) => (
              <div
                key={name}
                className={`px-4 py-2 cursor-pointer transition-colors ${index === highlightedIndex
                  ? "bg-primary text-white"
                  : "hover:bg-primary-50"
                  }`}
                //onClick={() => handleSelect(name)}
                onMouseDown={() => handleSelect(name)}
              >
                {name}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const DragAndDrop = ({ onChange, title = "Keywords", data, selectedTitle }) => {

  const [keywords, setKeywords] = useState([""]);
  const [connectors, setConnectors] = useState([]);

  const [displayString, setDisplayString] = useState("");
  const [keywordInput, setKeywordInput] = useState([""]);

  const [draggedIndex, setDraggedIndex] = useState(null);
  const [keywordString, setKeywordString] = useState([
    { keyword: "", connector: `${selectedTitle}` },
  ]);
  useEffect(() => {
    if (selectedTitle) {
      setKeywordString([{ keyword: selectedTitle, connector: "" }]);
    }
  }, [selectedTitle]);

  useEffect(() => {
    const isValidFormat = (arr) =>
      Array.isArray(arr) &&
      arr.every(
        (item, index) =>
          typeof item === "object" &&
          item !== null &&
          typeof item.keyword === "string" &&
          ["", "AND", "OR", "NOT"].includes(item.connector)
      );

    if (data && isValidFormat(data)) {
      if (data.length >= 1) {
        setKeywordString(data);
        setKeywords(data.map((item) => item.keyword));
        setConnectors(data.map((item) => item.connector).filter((c) => c !== ""));
      }
    } else {
      console.warn("Invalid data format: ", data);
    }
  }, [data]);

  useEffect(() => {
    const display = keywordString
      .map(
        (item) =>
          `${item.connector ? item.connector + " " : ""}"${item.keyword}"`
      )
      .join(" ")
      .trim();
    setDisplayString(display);
  }, [keywordString]);

  useEffect(() => {
    if (onChange) {
      onChange(keywords, connectors, keywordString);
    }
  }, [keywords, connectors, keywordString]);

  const handleDragStart = (index) => {
    setDraggedIndex(index);
  };

  const handleDrop = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    const updated = [...keywords];
    const temp = updated[index];
    updated[index] = updated[draggedIndex];
    updated[draggedIndex] = temp;

    let newKeywordString = [...keywordString];
    newKeywordString[index] = {
      ...keywordString[index],
      keyword: updated[index],
    };
    newKeywordString[draggedIndex] = {
      ...keywordString[draggedIndex],
      keyword: temp,
    };

    setKeywords(updated);
    setKeywordString(newKeywordString);
    setDraggedIndex(null);
  };

  const handleRemove = (index) => {
    let newKeywords = [...keywords];
    newKeywords[index] = "";
    setKeywords(newKeywords);

    let newKeywordString = [...keywordString];
    newKeywordString[index] = { ...keywordString[index], keyword: "" };
    setKeywordString(newKeywordString);
  };

  const handleToggleConnector = (index) => {
    const options = ["AND", "OR", "NOT"];
    setConnectors((prev) =>
      prev.map((val, i) =>
        i === index ? options[(options.indexOf(val) + 1) % options.length] : val
      )
    );

    let newKeywordString = [...keywordString];
    newKeywordString[index + 1] = {
      ...(keywordString[index + 1] || { keyword: "", connector: "" }),
      connector:
        options[(options.indexOf(connectors[index]) + 1) % options.length],
    };
    setKeywordString(newKeywordString);
  };

  const handleStringChange = (e, index) => {
    let newKeywordsInput = [...keywordInput];
    newKeywordsInput[index] = e;
    setKeywordInput(newKeywordsInput);
  };

  const handleAddKeyword = () => {
    setKeywords((prev) => [...prev, ""]);
    setConnectors((prev) => [...prev, "OR"]);
    setKeywordInput((prev) => [...prev, ""]);
    setKeywordString((prev) => [...prev, { keyword: "", connector: "OR" }]);
  };

  const handleKeywordStringChange = (e) => {
    const value = e;
    setDisplayString(value);
  };

  const handleFocusKeyword = (index) => {
    let newKeywords = [...keywords];
    newKeywords[index] = keywordInput[index] || "";
    setKeywords(newKeywords);

    let newKeywordsInput = [...keywordInput];
    newKeywordsInput[index] = "";
    setKeywordInput(newKeywordsInput);

    let newKeywordString = [...keywordString];
    newKeywordString[index] = {
      ...keywordString[index],
      keyword: keywordInput[index] || "",
    };
    setKeywordString(newKeywordString);
  };
  // console.log(selectedTitle)
  const handleBlurString = () => {
    const quoted = [];
    const unquoted = [];

    const regex = /"([^"]*)"|\b(AND|OR|NOT)\b/gi;
    let match;

    while ((match = regex.exec(displayString)) !== null) {
      if (match[1] !== undefined) {
        quoted.push(match[1]); // word inside quotes (can be empty string)
      } else if (match[2] !== undefined) {
        unquoted.push(match[2].toUpperCase()); // normalize connector
      }
    }

    const updatedKeywordString = quoted.map((kw, i) => ({
      keyword: kw,
      connector: i === 0 ? "" : unquoted[i - 1] || "OR",
    }));

    setKeywordString(updatedKeywordString);
  };

  return (
    <div className="flex flex-col gap-3">
      <h6>{title}</h6>
      <div className="flex items-center gap-1 flex-wrap">
        {keywords.map((keyword, index) => (
          <React.Fragment key={index}>
            <div
              draggable
              onDragStart={() => handleDragStart(index)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => handleDrop(e, index)}
              className="flex items-center h-[38px] w-[163px] gap-2 px-2 py-2 rounded-lg border-2 border-gray-300 my-2"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                height="20px"
                viewBox="0 -960 960 960"
                width="20px"
                fill="#000000"
                className="cursor-move"
              >
                <path d="M359.79-192Q330-192 309-213.21t-21-51Q288-294 309.21-315t51-21Q390-336 411-314.79t21 51Q432-234 410.79-213t-51 21Zm240 0Q570-192 549-213.21t-21-51Q528-294 549.21-315t51-21Q630-336 651-314.79t21 51Q672-234 650.79-213t-51 21Zm-240-216Q330-408 309-429.21t-21-51Q288-510 309.21-531t51-21Q390-552 411-530.79t21 51Q432-450 410.79-429t-51 21Zm240 0Q570-408 549-429.21t-21-51Q528-510 549.21-531t51-21Q630-552 651-530.79t21 51Q672-450 650.79-429t-51 21Zm-240-216Q330-624 309-645.21t-21-51Q288-726 309.21-747t51-21Q390-768 411-746.79t21 51Q432-666 410.79-645t-51 21Zm240 0Q570-624 549-645.21t-21-51Q528-726 549.21-747t51-21Q630-768 651-746.79t21 51Q672-666 650.79-645t-51 21Z" />
              </svg>

              {keyword && keyword !== "" ? (
                <Chip
                  label={keyword}
                  className="rounded-md justify-between text-gray !bg-gray-50 !border-info-100 flex-1"
                  size="sm"
                  onDelete={() => {
                    handleRemove(index);
                  }}
                />
              ) : (
                <div onBlur={() => handleFocusKeyword(index)}>
                  {/* <FormFieldSet
                    type="text"
                    size="sm"
                    value={keywordInput[index]}
                    placeholder="Enter Keyword"
                    onChange={(e) => handleStringChange(e, index)}
                    noTitle
                  /> */}
                  <CustomSearchNew
                    value={keywordInput[index]}
                    onChange={(e) => handleStringChange(e, index)}
                    //onFilteredCountChange={(count) => setFilteredCount(count)}
                    names={[
                      "ReactJS",
                      "Javascript",
                      "Tailwind",
                      "Mongodb",
                      "Python",
                    ]}
                    title={selectedTitle}
                  />
                </div>
              )}
            </div>

            {index < connectors.length && (
              <Button
                title={connectors[index]}
                variant="text"
                color="gray"
                size="sm"
                onClick={() => handleToggleConnector(index)}
                className="font-bold"
              />
            )}
          </React.Fragment>
        ))}

        <div className="flex items-center gap-2 m-3">
          <span className="font-semibold">OR</span>
          <Button
            title="Add Keyword"
            variant="outlined"
            size="sm"
            onClick={() => handleAddKeyword()}
            disabled={keywords.length >= 30}
          />
        </div>
      </div>

      {/* <div className="flex items-center gap-5 my-3">
        <span className="font-semibold">OR</span>
        <Button
          title="Add Keyword"
          variant="outlined"
          size="sm"
          onClick={() => handleAddKeyword()}
          disabled={keywords.length >= 30}
        />
      </div> */}

      <div className="my-5" onBlur={handleBlurString}>
        <FormFieldSet
          type="text"
          value={displayString}
          onChange={handleKeywordStringChange}
          noTitle
        />
      </div>
    </div>
  );
};

export default DragAndDrop;
